import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import UsersCell from "layouts/Flows/components/UsersCell";
import TextWithEllipsis from "layouts/pages/logs/components/TextWithEllipsis";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { useSlugCredential } from "hooks/useSlugCredential";
import { usePortalFlows } from "hooks/usePortalFlows";
import { useUserFlows } from "hooks/useUserFlows";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import defaultFlowIcon from "assets/images/for-flow/flow-icon-list-view.svg";
import { getResourceDependenciesWithHop } from "api/ResourceDependenciesApi";

export const Flows = () => {
   const [searchName, setSearchName] = useState("");
   const { credential } = useSlugCredential();
   const { flows } = usePortalFlows();
   const { userFlows } = useUserFlows();
   const { user } = useAuth0();
   const { users } = useSelector((store) => store?.portal?.users);
   const navigate = useNavigate();
   const [credentialFlowIds, setCredentialFlowIds] = useState([]);

   const getStatus = (flow) => {
      if (flow.trashed) return "deleted";
      if (flow.shared_status === "test") return "test";
      if (flow.steps === "") return "draft";
      return "active";
   };

   const tableData = useMemo(() => {
      const filteredFlows = flows
         .filter((flow) => credentialFlowIds.includes(flow.id))
         .filter((flow) => flow.name?.toLowerCase().includes(searchName.toLowerCase()));

      return {
         columns: [
            { Header: "flowId", accessor: "flowId", show: false },
            {
               Header: "icon",
               accessor: "icon",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value }) => (
                  <SoftBox width="60px">
                     <SoftBox component="img" width="100%" minHeight="60px" border="0.5px solid #E6E6E6" borderRadius="12px" sx={{ objectFit: "cover" }} src={value} />
                  </SoftBox>
               )
            },
            {
               Header: "name",
               accessor: "nameWithBadge",
               width: 250,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox
                     className="white-to-dark"
                     fontSize="14px"
                     fontWeight="bold"
                     display="flex"
                     alignItems="center"
                     sx={{ minWidth: "150px", maxWidth: "200px", whiteSpace: "normal", cursor: "pointer", wordBreak: "break-all" }}
                     onClick={() => navigate(`/flow/${value?.id}`)}
                  >
                     {value?.name?.length > 24 ? value?.name?.slice(0, 24) + "..." : value?.name}
                  </SoftBox>
               )
            },
            {
               Header: "description",
               accessor: "description",
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox>
                     <TextWithEllipsis value={{ name: value, maxWidth: "clamp(100px, 20vw, 300px)" }} />
                  </SoftBox>
               )
            },
            { Header: "name", accessor: "name", canGroupBy: false, show: false },
            {
               Header: "status",
               accessor: "status",
               canGroupBy: false,
               Cell: ({ value }) => <StatusBadge status={value} />
            },
            { Header: "shared status", accessor: "sharedStatus", canGroupBy: true, expanded: true },
            {
               Header: "users",
               accessor: "users",
               canGroupBy: false,
               Cell: ({ value }) => <UsersCell users={value} />,
               disableSortBy: true
            },
            { Header: "last edited", accessor: "lastEdited", width: 170, canGroupBy: false, disableSortBy: false }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: filteredFlows.map((item) => {
            const flowOwner = users?.filter((portalUser) => item.created_by_id?.includes(portalUser.auth0_id)) || [];
            const sharedToUsers = userFlows?.filter((flow) => flow.flow_id === item.id)?.map((sharedFlow) => sharedFlow.user_id);
            const sharedUsers = users?.filter((portalUser) => sharedToUsers?.includes(portalUser.auth0_id)) || [];

            return {
               flowId: item.id,
               nameWithBadge: { trashed: item.trashed, name: item.name, id: item.id },
               name: item.name,
               sharedStatus: item.created_by_id === user?.cxfUserId ? "Owner" : "",
               status: getStatus(item),
               users: [...flowOwner, ...sharedUsers],
               lastEdited: formatDateAndTime(getLocalDateTimeString(item.updated_date_time || item.created_date_time)) || "",
               description: item?.description,
               icon: item.logo ? item.logo : defaultFlowIcon
            };
         })
      };
   }, [
      flows,
      credentialFlowIds,
      userFlows,
      users,
      searchName,
      user?.cxfUserId
   ]);

   useEffect(() => {
      const fetchCredentialFlowDependencies = async () => {
         if (!user?.stripeId || !credential?.id) {
            setCredentialFlowIds([]);
            return;
         }

         try {
            const dependenciesResponse = await getResourceDependenciesWithHop({
               tenantId: user.stripeId,
               sourceId: credential.id,
               direction: "reverse",
               directTargetResourceType: "flow",
               throughTargetResourceType: "connector",
               finalTargetResourceType: "flow",
               targetIdFromContextId: true
            });
            const nextFlowIds = Array.isArray(dependenciesResponse?.resultObject)
               ? dependenciesResponse.resultObject.map((dependency) => dependency.target_id)
               : [];

            setCredentialFlowIds([...new Set(nextFlowIds)]);
         } catch (error) {
            console.error(error);
            setCredentialFlowIds([]);
         }
      };

      fetchCredentialFlowDependencies();
   }, [user?.stripeId, credential?.id]);

   return (
      <SoftBox>
         <SoftBox sx={{ alignItems: "flex-start", display: "flex", justifyContent: "flex-start", padding: "12px 0 12px 40px" }}>
            <SoftInput size="medium" placeholder="Search..." value={searchName} onChange={({ currentTarget }) => setSearchName(currentTarget.value)} sx={{ maxWidth: "380px", marginRight: "34px" }} />
         </SoftBox>
         <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons tableView="list" noTopPart tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
      </SoftBox>
   );
};
